<template>
    <div class="date-month-year-picker" ref="root">
        <div class="date-month-year-picker-trigger" :class="{ 'date-month-year-picker-trigger--disabled': disabled }">
            <button class="date-month-year-picker-nav" :disabled="disabled || currentMode === 'period'" @click="prev">
                <i class="fas fa-chevron-left"></i>
            </button>
            <button class="date-month-year-picker-label" :disabled="disabled" @click="open = !open">
                <i class="far fa-calendar date-month-year-picker-label-icon"></i>
                {{ label }}
            </button>
            <button class="date-month-year-picker-nav" :disabled="disabled || currentMode === 'period'" @click="next">
                <i class="fas fa-chevron-right"></i>
            </button>
        </div>

        <div v-if="open" class="date-month-year-picker-dropdown">
            <div class="date-month-year-picker-tabs">
                <button
                    :class="['date-month-year-picker-tab', { 'date-month-year-picker-tab--active': currentMode === 'month' }]"
                    @click="switchToMonth">Mês</button>
                <button
                    :class="['date-month-year-picker-tab', { 'date-month-year-picker-tab--active': currentMode === 'period' }]"
                    @click="currentMode = 'period'">Período</button>
            </div>

            <div v-if="currentMode === 'month'">
                <div class="date-month-year-picker-year-nav">
                    <button class="date-month-year-picker-year-btn" @click="draftYear -= 1">
                        <i class="fas fa-chevron-left"></i>
                    </button>
                    <span class="date-month-year-picker-year-label">{{ draftYear }}</span>
                    <button class="date-month-year-picker-year-btn" @click="draftYear += 1">
                        <i class="fas fa-chevron-right"></i>
                    </button>
                </div>

                <div class="date-month-year-picker-months">
                    <button v-for="(name, idx) in monthsShort" :key="idx"
                        :class="['date-month-year-picker-month-btn', { 'date-month-year-picker-month-btn--selected': isMonthSelected(idx + 1) }]"
                        @click="selectMonth(idx + 1)">{{ name }}</button>
                </div>

                <div class="date-month-year-picker-footer">
                    <Button type="primary" size="small" title="Hoje" :clicked="goToday" />
                    <Button type="danger" size="small" title="Fechar" :clicked="closeDropdown" />
                </div>
            </div>

            <div v-else>
                <div class="date-month-year-picker-period-row">
                    <label class="date-month-year-picker-period-label">De</label>
                    <input class="date-month-year-picker-date-input" type="date" :value="draftStart"
                        :max="draftEnd || undefined" :disabled="disabled" @input="draftStart = $event.target.value" />
                </div>
                <div class="date-month-year-picker-period-row">
                    <label class="date-month-year-picker-period-label">Até</label>
                    <input class="date-month-year-picker-date-input" type="date" :value="draftEnd"
                        :min="draftStart || undefined" :disabled="disabled" @input="draftEnd = $event.target.value" />
                </div>
                <p v-if="periodError" class="date-month-year-picker-error">{{ periodError }}</p>

                <div class="date-month-year-picker-presets">
                    <button class="date-month-year-picker-preset-btn" @click="applyPreset('today')">Hoje</button>
                    <button class="date-month-year-picker-preset-btn" @click="applyPreset('7d')">Últimos 7 dias</button>
                    <button class="date-month-year-picker-preset-btn" @click="applyPreset('30d')">Últimos 30
                        dias</button>
                    <button class="date-month-year-picker-preset-btn" @click="applyPreset('month')">Mês atual</button>
                </div>

                <div class="date-month-year-picker-footer">
                    <Button type="danger" size="small" title="Cancelar" :clicked="closeDropdown" />
                    <Button type="success" size="small" title="Aplicar" :disabled="!canApplyPeriod"
                        :clicked="applyPeriod" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Button from "@/ui/forms/Button";

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const MONTHS_SHORT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function toIso(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

export function formatBrFromIso(iso) {
    const [y, m, d] = (iso || "").split("-");
    if (!y || !m || !d) return "";
    return `${d}/${m}/${y}`;
}

export function buildDueDateMetaFromIsoRange(startDate, endDate) {
    const originalValue = [
        formatBrFromIso(startDate),
        formatBrFromIso(endDate),
    ].filter(Boolean);

    return {
        originalValue,
        value: originalValue.length ? originalValue.join("<>") : "",
    };
}

export function buildDueDateMetaFromMonth(year, month) {
    if (!year || !month) {
        return { originalValue: [], value: "" };
    }

    const startDate = toIso(new Date(year, month - 1, 1));
    const endDate = toIso(new Date(year, month, 0));

    return buildDueDateMetaFromIsoRange(startDate, endDate);
}

export function buildDueDateMetaForToday() {
    const todayIso = toIso(new Date());
    return buildDueDateMetaFromIsoRange(todayIso, todayIso);
}

export function buildDueDateMetaForAll() {
    return { originalValue: [], value: "" };
}

export function applyDueDateMetaToStringFilter({
    stringFilter,
    dueDateMeta,
    routeName,
    field = "_DueDate",
    title = "Data de Vencimento",
    type = "date",
}) {
    const currentFilters = Array.isArray(stringFilter) ? stringFilter : [];
    const isCurrentDueDateFilter = (item) => item?.field === field && item?.title === title;
    const dueDateFilter = currentFilters.find((item) => isCurrentDueDateFilter(item)) || {};
    const filtersWithoutDueDate = currentFilters.filter((item) => !isCurrentDueDateFilter(item));

    return [
        ...filtersWithoutDueDate,
        {
            ...dueDateFilter,
            routeName,
            title,
            field,
            type,
            originalValue: dueDateMeta.originalValue,
            value: dueDateMeta.value,
        },
    ];
}

export default {
    name: "DateMonthYearPicker",
    components: { Button },
    props: {
        year: { type: Number, default: 0 },
        month: { type: Number, default: 0 },
        mode: { type: String, default: "month" },
        startDate: { type: String, default: "" },
        endDate: { type: String, default: "" },
        stringFilter: {
            type: Array,
            default: () => [],
        },
        filterField: { type: String, default: "_DueDate" },
        filterTitle: { type: String, default: "Data de Vencimento" },
        filterType: { type: String, default: "date" },
        filterRouteName: { type: String, default: "" },
        disabled: { type: Boolean, default: false },
    },
    data() {
        return {
            open: false,
            currentMode: this.mode,
            draftYear: this.year,
            draftStart: this.startDate,
            draftEnd: this.endDate,
            periodError: "",
            monthsShort: MONTHS_SHORT,
        };
    },
    watch: {
        mode(val) { this.currentMode = val; },
        startDate(val) { this.draftStart = val; },
        endDate(val) { this.draftEnd = val; },
        disabled(val) { if (val) this.open = false; },
        open(isOpen) {
            if (isOpen) {
                this.draftYear = this.year;
                this.draftStart = this.startDate;
                this.draftEnd = this.endDate;
                this.periodError = "";
            }
        },
    },
    computed: {
        label() {
            if (this.currentMode === "period" && this.startDate && this.endDate) {
                return `${this.formatBr(this.startDate)} - ${this.formatBr(this.endDate)}`;
            }
            if (!this.year || !this.month) return "Carregando...";
            return `${MONTHS[this.month - 1]} / ${this.year}`;
        },
        canApplyPeriod() {
            return this.draftStart && this.draftEnd && this.draftStart <= this.draftEnd;
        },
    },
    mounted() {
        document.addEventListener("click", this.onClickOutside);
        if (!this.year || !this.month) {
            const now = new Date();
            this.$emit("update:year", now.getFullYear());
            this.$emit("update:month", now.getMonth() + 1);
        }
    },
    beforeDestroy() {
        document.removeEventListener("click", this.onClickOutside);
    },
    methods: {
        onClickOutside(e) {
            if (this.$refs.root && !this.$refs.root.contains(e.target)) {
                this.open = false;
            }
        },
        closeDropdown() {
            this.open = false;
        },
        switchToMonth() {
            this.currentMode = "month";
            if (!this.year || !this.month) {
                const now = new Date();
                this.emitMonth(now.getFullYear(), now.getMonth() + 1);
            }
        },
        formatBr(iso) {
            const [y, m, d] = iso.split("-");
            if (!y || !m || !d) return iso;
            return `${d}/${m}/${y}`;
        },
        toIso(date) {
            return toIso(date);
        },
        buildStringFilter(dueDateMeta) {
            return applyDueDateMetaToStringFilter({
                stringFilter: this.stringFilter,
                dueDateMeta,
                routeName: this.filterRouteName || this.$route.name,
                field: this.filterField,
                title: this.filterTitle,
                type: this.filterType,
            });
        },
        emitMonth(year, month) {
            if (year !== this.year) this.$emit("update:year", year);
            if (month !== this.month) this.$emit("update:month", month);
            if (this.startDate) this.$emit("update:startDate", "");
            if (this.endDate) this.$emit("update:endDate", "");
            if (this.currentMode !== "month") {
                this.currentMode = "month";
                this.$emit("update:mode", "month");
            }
            const dueDateMeta = buildDueDateMetaFromMonth(year, month);
            this.$emit("change", {
                mode: "month",
                year,
                month,
                dueDateMeta,
                stringFilter: this.buildStringFilter(dueDateMeta),
            });
        },
        emitPeriod(startDate, endDate) {
            if (startDate !== this.startDate) this.$emit("update:startDate", startDate);
            if (endDate !== this.endDate) this.$emit("update:endDate", endDate);
            if (this.year !== 0) this.$emit("update:year", 0);
            if (this.month !== 0) this.$emit("update:month", 0);
            if (this.currentMode !== "period") {
                this.currentMode = "period";
                this.$emit("update:mode", "period");
            }
            const dueDateMeta = buildDueDateMetaFromIsoRange(startDate, endDate);
            this.$emit("change", {
                mode: "period",
                startDate,
                endDate,
                dueDateMeta,
                stringFilter: this.buildStringFilter(dueDateMeta),
            });
        },
        prev() {
            let m = this.month - 1;
            let y = this.year;
            if (m < 1) { m = 12; y -= 1; }
            this.emitMonth(y, m);
        },
        next() {
            let m = this.month + 1;
            let y = this.year;
            if (m > 12) { m = 1; y += 1; }
            this.emitMonth(y, m);
        },
        selectMonth(m) {
            this.emitMonth(this.draftYear, m);
            this.open = false;
        },
        goToday() {
            const now = new Date();
            this.emitMonth(now.getFullYear(), now.getMonth() + 1);
            this.open = false;
        },
        isMonthSelected(m) {
            return this.currentMode === "month" && this.draftYear === this.year && m === this.month;
        },
        applyPreset(preset) {
            const today = new Date();
            let start = new Date(today);
            const end = new Date(today);
            if (preset === "7d") {
                start.setDate(today.getDate() - 6);
            } else if (preset === "30d") {
                start.setDate(today.getDate() - 29);
            } else if (preset === "month") {
                start = new Date(today.getFullYear(), today.getMonth(), 1);
                end.setMonth(today.getMonth() + 1, 0);
            }
            this.draftStart = this.toIso(start);
            this.draftEnd = this.toIso(end);
        },
        applyPeriod() {
            if (!this.canApplyPeriod) {
                this.periodError = "Período inválido";
                return;
            }
            this.emitPeriod(this.draftStart, this.draftEnd);
            this.open = false;
        },
    },
};
</script>

<style scoped>
.date-month-year-picker {
    position: relative;
    display: inline-block;
}

.date-month-year-picker-trigger {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--bg-base);
    border: 1px solid var(--border-input);
    border-radius: 50px;
    padding: 0 10px;
    height: 32px;
}

.date-month-year-picker-trigger--disabled {
    opacity: 0.5;
    pointer-events: none;
}

.date-month-year-picker-nav {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 11px;
    transition: background 0.15s;
}

.date-month-year-picker-nav:hover:not(:disabled) {
    background: var(--bg-hover);
    color: var(--text-primary);
}

.date-month-year-picker-nav:disabled {
    opacity: 0.4;
    cursor: default;
}

.date-month-year-picker-label {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0 8px;
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0.5px;
    white-space: nowrap;
    min-width: 160px;
    transition: color 0.15s;
}

.date-month-year-picker-label:hover:not(:disabled) {
    color: var(--color-brand);
}

.date-month-year-picker-label:disabled {
    cursor: default;
}

.date-month-year-picker-label-icon {
    color: var(--text-muted);
    margin-right: 4px;
    font-size: 11px;
}

.date-month-year-picker-dropdown {
    position: absolute;
    z-index: 1000;
    top: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: var(--bg-base);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    padding: 12px;
    width: 300px;
    box-shadow: var(--shadow-lg);
}

.date-month-year-picker-tabs {
    display: flex;
    gap: 4px;
    background: var(--bg-elevated);
    border-radius: 6px;
    padding: 4px;
    margin-bottom: 12px;
}

.date-month-year-picker-tab {
    flex: 1;
    background: none;
    border: none;
    border-radius: 4px;
    padding: 6px 0;
    font-size: 12px;
    cursor: pointer;
    color: var(--text-secondary);
    transition: background 0.15s, color 0.15s;
}

.date-month-year-picker-tab:hover {
    color: var(--text-primary);
}

.date-month-year-picker-tab--active {
    background: var(--bg-base);
    color: var(--text-primary);
    font-weight: 500;
    box-shadow: var(--shadow-sm);
}

.date-month-year-picker-year-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.date-month-year-picker-year-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    color: var(--text-secondary);
    font-size: 11px;
    transition: background 0.15s;
}

.date-month-year-picker-year-btn:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
}

.date-month-year-picker-year-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
}

.date-month-year-picker-months {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    margin-bottom: 12px;
}

.date-month-year-picker-month-btn {
    background: none;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 6px 0;
    font-size: 13px;
    cursor: pointer;
    color: var(--text-primary);
    transition: background 0.15s;
}

.date-month-year-picker-month-btn:hover {
    background: var(--bg-hover);
}

.date-month-year-picker-month-btn--selected {
    background: var(--color-brand);
    color: var(--text-inverse);
    border-color: var(--color-brand);
}

.date-month-year-picker-month-btn--selected:hover {
    background: var(--color-brand-hover);
}

.date-month-year-picker-footer {
    display: flex;
    justify-content: space-between;
    padding-top: 10px;
    border-top: 1px solid var(--border-subtle);
    margin-top: 4px;
}

.date-month-year-picker-period-row {
    margin-bottom: 8px;
}

.date-month-year-picker-period-label {
    display: block;
    font-size: 11px;
    color: var(--text-secondary);
    margin-bottom: 4px;
}

.date-month-year-picker-presets {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
    margin-bottom: 4px;
}

.date-month-year-picker-preset-btn {
    background: none;
    border: 1px solid var(--border-color);
    border-radius: 50px;
    padding: 3px 10px;
    font-size: 11px;
    cursor: pointer;
    color: var(--text-secondary);
    transition: background 0.15s, color 0.15s;
}

.date-month-year-picker-preset-btn:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
}

.date-month-year-picker-error {
    font-size: 11px;
    color: var(--color-danger);
    margin-top: 4px;
}

.date-month-year-picker-date-input {
    width: 100%;
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--border-input);
    border-radius: 6px;
    background: var(--input-bg);
    color: var(--input-color);
    font-size: 13px;
    outline: none;
    cursor: pointer;
    transition: border-color 0.15s;
}

.date-month-year-picker-date-input:focus {
    border-color: var(--color-brand);
}

.date-month-year-picker-date-input:disabled {
    background: var(--input-disabled-bg);
    cursor: default;
    opacity: 0.6;
}

.date-month-year-picker-date-input::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
}
</style>
